import React from 'react';
import { clsx } from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
}

export function Input({
  label,
  error,
  icon,
  className,
  ...props
}: InputProps) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
            {icon}
          </div>
        )}
        <input
          className={clsx(
            'w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30',
            'focus:outline-none focus:border-foretell-accent transition-colors',
            'font-medium',
            icon && 'pl-11',
            error && 'border-rose-500/50',
            className
          )}
          {...props}
        />
      </div>
      {error && (
        <p className="text-xs text-rose-400">{error}</p>
      )}
    </div>
  );
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  showCount?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ label, error, showCount, maxLength, value, className, ...props }, ref) {
    const currentLength = typeof value === 'string' ? value.length : 0;

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest">
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            className={clsx(
              'w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30',
              'focus:outline-none focus:border-foretell-accent transition-colors',
              'font-medium resize-none',
              error && 'border-rose-500/50',
              className
            )}
            maxLength={maxLength}
            value={value}
            {...props}
          />
          {showCount && maxLength && (
            <div className="absolute bottom-3 right-4 text-[10px] font-mono text-white/20">
              {currentLength}/{maxLength}
            </div>
          )}
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
      </div>
    );
  }
);

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: Array<{ value: string; label: string }>;
}

export function Select({
  label,
  options,
  className,
  ...props
}: SelectProps) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest">
          {label}
        </label>
      )}
      <select
        className={clsx(
          'w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white',
          'focus:outline-none focus:border-foretell-accent transition-colors',
          'font-medium cursor-pointer appearance-none',
          className
        )}
        {...props}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-foretell-card">
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
